import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../../assets/Icons";
import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import { taxCompliantAccountDetail } from "../../../../../redux/features/accounts/taxAccount/taxAccountDetails";
import { beneBankLookUp } from "../../../../../redux/features/beneficiary/beneAllVerification/beneAllVerification";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import { toast_position } from "../../../../../redux/features/Settings/Authorization";
import { beneLookUp } from "../../../../../redux/features/verifyPAN/verifyPAN";
import { GlobalConfiguration } from "../../../../../utils/configurationProvider";

const TaxationEditAccountPANSuccess = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const updatedFields = location?.state?.updatedFields || [];
  const [taxAccountCurrentDetails, setTaxAccountCurrentDetails] = useState({
    id: "",
    name: "",
    bank_name: "",
    bank_acc_no: "",
    ifsc_code: "",
    category: "",
    pan: "",
    labels: "",
  });

  const benebankLookData = useSelector(
    (state) => state.beneAllVerification.beneLookData,
  );
  const beneLookData = useSelector((state) => state.verify.beneLookData);

  const { taxCompliantDetailsData, taxCompliantDetailsLoading } = useSelector(
    (state) => state.taxCompliantAccountDetail,
  );

  const tdsAccount = useMemo(
    () => taxCompliantDetailsData?.tds_accounts?.[0] || {},
    [taxCompliantDetailsData?.tds_accounts],
  );

  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));
    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
    };
  }, [dispatch]);

  useEffect(() => {
    const fetchPANSuccessDetail = async () => {
      let response = await dispatch(taxCompliantAccountDetail(id));
      if (response?.error?.message === "Request failed with status code 404") {
        navigate("*");
      }
    };
    fetchPANSuccessDetail();
  }, [dispatch, id, navigate]);

  useEffect(() => {
    if (
      tdsAccount &&
      Object.keys(tdsAccount).length > GlobalConfiguration.lengthCheckZero
    ) {
      const {
        id: taxAccountId,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category,
        pan_no,
      } = tdsAccount;
      setTaxAccountCurrentDetails({
        id: taxAccountId,
        name,
        bank_name,
        bank_acc_no,
        ifsc_code,
        category,
        pan: pan_no,
        labels: tdsAccount.labels,
      });
    }
  }, [tdsAccount]);

  const pan = taxCompliantDetailsData?.tds_accounts?.[0]?.pan_no;
  const ifsc = taxCompliantDetailsData?.tds_accounts?.[0]?.ifsc_code;
  const acc = taxCompliantDetailsData?.tds_accounts?.[0]?.bank_acc_no;

  useEffect(() => {
    if (!pan || !ifsc || !acc) return;

    dispatch(beneLookUp({ pan_number: pan }));
    dispatch(beneBankLookUp({ ifsc, bank_account_no: acc }));
  }, [pan, ifsc, acc]);

  const handlers = {
    createAccount: () => navigate("/tax_account_dashboard/create_tax_account"),
    createTxn: () => toast.info("Feature coming soon!", toast_position),
    viewDetails: () =>
      navigate(
        `/tax_account_dashboard/tax_account_view_all/tax_account_detail_page/${id}`,
        {
          replace: true,
        },
      ),
    dashboard: () => navigate("/tax_account_dashboard"),
  };

  const fieldsToShow =
    updatedFields.length > GlobalConfiguration.lengthCheckZero
      ? updatedFields
      : ["pan_no", "gstin_detail"];

  const panGstFieldConfig = {
    pan_no: {
      leftSubTitle: "PAN number",
      getTitle: (acc) => acc?.pan_no,
    },
    gstin_detail: {
      leftSubTitle: "GSTIN (optional)",
      getTitle: (acc) => acc?.gstin_detail,
    },
  };

  const getPanGstSuccessTitle = () => {
    if (fieldsToShow.length === GlobalConfiguration?.lengthCheckOne) {
      return fieldsToShow[0] === "gstin_detail"
        ? "GST number updated successfully."
        : "PAN number updated successfully.";
    }

    return "PAN and GST number updated successfully.";
  };

  const renderPanGstCards = () => {
    return fieldsToShow
      .filter((key) => panGstFieldConfig[key])
      .filter((key) => key !== "gstin_detail" || tdsAccount?.gstin_detail)
      .map((key) => {
        const config = panGstFieldConfig[key];
        const title = config.getTitle(tdsAccount);
        if (key === "gstin_detail" && !title) return null;
        return (
          <InfoCard
            key={key}
            bgColor="var(--positive-card)"
            rows={[
              {
                leftTitle: title || "Not Given",
                leftSubTitle: config.leftSubTitle,
              },
            ]}
          />
        );
      });
  };

  const primaryBtn = {
    enabledBgColor: "var(--text-primary)",
    enabledTextColor: "var(--positive-bg)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    imgColor: "var(--positive-bg)",
  };

  const outlineBtn = {
    enabledBgColor: "var(--bg-positive-bg)",
    enabledTextColor: "var(--text-primary)",
    iconToBeAlign: "left",
    fullWidthRequired: true,
    outLineColor: "var(--text-primary)",
  };

  const buttonRows = [
    [
      {
        ...primaryBtn,
        btnText: "Create account",
        icon: ICONS.SELECTUSER,
        handleFunction: handlers.createAccount,
      },
      {
        ...primaryBtn,
        btnText: "Create transaction",
        icon: ICONS.ADD_BUTTON,
        handleFunction: handlers.createTxn,
      },
      {
        ...primaryBtn,
        btnText: "View details",
        icon: ICONS.VIEW_DETAILS,
        handleFunction: handlers.viewDetails,
      },
    ],
    [
      {
        ...outlineBtn,
        btnText: "Go to dashboard",
        icon: ICONS.DASHBOARD,
        handleFunction: handlers.dashboard,
      },
    ],
  ];
  return (
    <div>
      <StatusContainer
        isLoading={taxCompliantDetailsLoading}
        status="success"
        buttonRows={buttonRows}
        title={getPanGstSuccessTitle()}
      >
        <TabsWeapperSection title="Account details">
          <>{renderPanGstCards()}</>
        </TabsWeapperSection>
        {beneLookData?.tds_accounts?.length >
          GlobalConfiguration.lengthCheckOne && (
          <TabsWeapperSection title="Accounts Associated to PAN">
            {beneLookData?.tds_accounts
              ?.filter(
                (accountData) =>
                  accountData?.approval_status === "pending" ||
                  accountData?.approval_status === "approved",
              )
              .map((accountData, id) => (
                <InfoCard
                  key={id}
                  bgColor="var(--positive-card)"
                  header={true}
                  borderColor="var(--positive-bg)"
                  rightHeaderText={`${
                    accountData?.approval_status?.charAt(0).toUpperCase() +
                    accountData?.approval_status?.slice(1)
                  } account`}
                  rightHeaderTextColor="var(--text-secondary)"
                  leftHeaderText={accountData?.bank_account_holder_full_name}
                  leftHeaderWidth="w-width-lg"
                  rows={[
                    {
                      leftTitle: accountData?.bank_acc_no,
                      leftSubTitle: "Bank account number",
                      ifsc: accountData?.ifsc_code,
                    },
                  ]}
                />
              ))}
          </TabsWeapperSection>
        )}
        {benebankLookData?.tds_accounts?.length >
          GlobalConfiguration.lengthCheckOne && (
          <TabsWeapperSection title="Accounts Associated to Bank">
            {benebankLookData?.tds_accounts
              .filter(
                (accountData) =>
                  accountData?.approval_status === "pending" ||
                  accountData?.approval_status === "approved",
              )
              .map((accountData, id) => (
                <InfoCard
                  key={id}
                  bgColor="var(--positive-card)"
                  header={true}
                  borderColor="var(--positive-bg)"
                  rightHeaderText={`${
                    accountData?.approval_status?.charAt(0).toUpperCase() +
                    accountData?.approval_status?.slice(1)
                  } account`}
                  rightHeaderTextColor="var(--text-secondary)"
                  leftHeaderText={accountData?.name}
                  leftHeaderWidth="w-width-lg"
                  rows={[
                    {
                      leftTitle: accountData?.pan_no,
                      leftSubTitle: "PAN number",
                    },
                  ]}
                />
              ))}
          </TabsWeapperSection>
        )}
      </StatusContainer>
    </div>
  );
};

export default TaxationEditAccountPANSuccess;
